/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */


/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,


	// Selectors
	selectors: {
		variantSelector: 'mm-product-variants',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const productLog = (level, message, data = {}) => {
	if (!PRODUCT_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛍️ [PRODUCT] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};

	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;

		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			productLog('info', 'Variant ID updated', {
				oldVariantId: oldValue,
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);

		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;

		elementsToUpdate.forEach((currentElement) => {
			let newElement = null;

			// Match by data-reload-id first (most reliable)
			const reloadId = currentElement.dataset.reloadId;
			if (reloadId) {
				newElement = html.querySelector(`[data-reload-id="${reloadId}"]`);
			}

			// Fallback: match by ID
			if (!newElement && currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			}

			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
				} catch (error) {
					productLog('warn', 'Element update failed', {
						reloadId,
						error: error.message
					});
				}
			}
		});

		productLog('info', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});

		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 AJAX + URL + MEDIA UPDATE ON VARIANT CHANGE
 */
let _variantAjaxDebounce = null;
const _variantAjaxCache = new Map();

document.addEventListener('product:variant-found', async (e) => {
	const variantId = e.detail?.data?.variantId;
	if (!variantId || e.target.closest?.('[data-mm-variants-modal]')) return;

	const selector = document.querySelector('mm-product-variants[data-section]');
	if (!selector) return; // not on product page

	clearTimeout(_variantAjaxDebounce);
	_variantAjaxDebounce = setTimeout(async () => {
		const baseUrl = selector.dataset.url || window.location.pathname;
		const sectionId = selector.dataset.section;

		// Update URL
		try {
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', variantId);
			window.history.replaceState({}, '', url.toString());
		} catch (_) {}

		// Update media
		if (window.MediaManager?.updateImagesVariant) {
			try {
				const jsonEl = selector.querySelector('script[type="application/json"]');
				if (jsonEl) {
					const variants = JSON.parse(jsonEl.textContent.trim());
					const variant = variants.find(v => String(v.id) === String(variantId));
					if (variant) window.MediaManager.updateImagesVariant(variant);
				}
			} catch (_) {}
		}

		// AJAX section reload
		const fetchUrl = `${baseUrl}?variant=${variantId}&section_id=${sectionId}`;
		let html;
		if (_variantAjaxCache.has(fetchUrl)) {
			html = _variantAjaxCache.get(fetchUrl);
		} else {
			try {
				const response = await fetch(fetchUrl);
				if (!response.ok) return;
				html = new DOMParser().parseFromString(await response.text(), 'text/html');
				_variantAjaxCache.set(fetchUrl, html);
			} catch (_) { return; }
		}

		const productForm = document.querySelector(PRODUCT_CONFIG.selectors.productForm);
		ProductManager.updateVariantId(productForm, variantId);
		ProductManager.updateReloadElements(html);
		// Le HTML fraîchement rendu porte l'état "limite atteinte" de la nouvelle variante
		window.MMPurchaseLimits?.syncFromDocument(html, productForm);
	}, PRODUCT_CONFIG.debounceDelay);
});


/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 * - Affiche le sticky CTA quand le CTA principal (add-to-cart) sort de la vue
 * - Cache le sticky CTA quand on entre dans le footer
 */
function initializeStickyCTA() {
	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;
	const footer = document.querySelector('footer, .shopify-section--footer');

	if (!classicCTA || !stickyCTA) {
		productLog('warn', 'StickyCTA: Required elements not found');
		return;
	}

	let addToCartVisible = true;
	let footerVisible = false;

	const updateStickyVisibility = () => {
		const shouldShow = !addToCartVisible && !footerVisible;
		// Mettre à jour tous les sticky (desktop + mobile)
		document.querySelectorAll('[data-sticky-cta]').forEach(sticky => {
			sticky.classList.toggle('is-visible', shouldShow);
		});
	};

	try {
		// Observer pour le CTA add-to-cart : cache le sticky quand il est visible
		const ctaObserver = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				addToCartVisible = entry.isIntersecting;
				updateStickyVisibility();
			});
		}, {
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});
		ctaObserver.observe(classicCTA);

		// Observer pour le footer : cache le sticky quand on entre dans le footer
		if (footer) {
			const footerObserver = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					footerVisible = entry.isIntersecting;
					updateStickyVisibility();
				});
			}, {
				threshold: 0,
				rootMargin: '0px'
			});
			footerObserver.observe(footer);
		}

		// Réappliquer la visibilité après un product-reload (changement de variante)
		document.addEventListener('product:product-reload-complete', updateStickyVisibility);

	} catch (error) {
		productLog('error', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/*********************************************************************************************
 * 👕 VARIANTES DE TAILLE — relais sticky CTA / drawer
 *
 * Le sticky CTA et le drawer vivent dans le MÊME <form> que le sélecteur principal. Y rendre
 * un second groupe de radios partageant le même `name` les rendrait mutuellement exclusifs :
 * cliquer une taille dans le sticky décocherait celle de la page. Les pastilles y sont donc
 * de simples boutons qui cliquent le vrai radio, puis se resynchronisent sur son état.
 */

function syncSizeProxies() {
	document.querySelectorAll('[data-size-proxy]').forEach(btn => {
		const input = findSizeInput(btn);
		const checked = !!input && input.checked;
		btn.setAttribute('aria-checked', String(checked));
	});
}

function findSizeInput(btn) {
	const form = btn.closest('form');
	if (!form) return null;

	// On vise les vrais radios : ceux du relais n'en ont pas (ce sont des <button>)
	return [...form.querySelectorAll('input[data-variant-input]')].find(
		input => input.name === `options[${btn.dataset.optionName}]` && input.value === btn.dataset.value
	) || null;
}

function initSizeVariantProxies() {
	if (!document.querySelector('[data-size-proxy]')) return;

	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-size-proxy]');
		if (!btn || btn.disabled) return;

		const input = findSizeInput(btn);
		if (input && !input.checked) input.click();
	});

	// Le relais suit la sélection, d'où qu'elle vienne (page, sticky ou drawer)
	document.addEventListener('product:variant-found', syncSizeProxies);
	document.addEventListener('product:product-reload-complete', syncSizeProxies);

	syncSizeProxies();
	productLog('info', 'Size variant proxies initialized');
}

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Utilities
 */

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const inventoryAttr = parseInt(input.dataset.variantInventory, 10);
	const maxValue = Number.isFinite(inventoryAttr) ? inventoryAttr : (parseInt(input.getAttribute('max'), 10) || 9999);
	const inputId = input.id;

	// Mettre à jour tous les boutons + (main + sticky)
	document.querySelectorAll(`[data-qtty-increase="${inputId}"]`).forEach(plusBtn => {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	});
}

function syncQuantityDisplay(input) {
	const value = input.value;
	document.querySelectorAll(`[data-qtty-display="${input.id}"]`).forEach(display => {
		display.textContent = value;
	});
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		// Initialize components — pas de fetch panier ici : le Liquid a déjà rendu le bon plafond
		initializeStickyCTA();
		initSizeVariantProxies();

		// Initialize button states and display on page load
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			updateQuantityButtonStates(input);
			syncQuantityDisplay(input);
		});

		// Variant keyboard: Enter on option value selects it
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' && e.target.classList.contains('mm-product-variants-option-value')) {
				const input = e.target.querySelector('[data-variant-input]');
				if (input && !input.checked) {
					input.click();
				}
			}
		});

		productEmit('initialized');
		productLog('info', 'Product page initialized successfully');
	} catch (error) {
		productLog('error', 'Failed to initialize product page', { error: error.message });
	}
});

// Re-adjust after variant change (reload updates the DOM including the input).
// Le HTML rechargé vient du serveur avec le bon plafond : aucun recalcul panier nécessaire ici.
document.addEventListener('product:product-reload-complete', () => {
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		const currentValue = parseInt(input.value, 10) || 1;
		const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

		if (currentValue > maxValue) {
			input.value = maxValue;
		}

		// Sync display span and button states after variant change
		syncQuantityDisplay(input);
		updateQuantityButtonStates(input);
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (PRODUCT_CONFIG.isDevelopment) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});
}
